import { FiLock, FiShield, FiInfo } from "react-icons/fi";
import trustCardBg from "../../assets/how-it-works-page/Built On Trust Hero Card.png";
import waveCardBg from "../../assets/how-it-works-page/Card 05_ Organic Contour Waves Texture Graphic Card.png";
import facadeImg from "../../assets/how-it-works-page/Simulated Architectural Facade.png";
import { useInView } from "../../hooks/useInView";

export function DataSafety() {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section className="w-full bg-white px-4 py-16 sm:py-20">
      <div className="mx-auto w-full max-w-[1344px]">
        <div
          ref={ref}
          className={`grid grid-cols-1 gap-8 lg:grid-cols-[496fr_711fr] ${inView ? "in-view" : ""}`}
        >
          {/* Left column */}
          <div className="flex flex-col gap-6">
            <div>
              <h2 className="reveal-scroll delay-1 text-3xl font-extrabold leading-tight text-[#1f153a] sm:text-4xl">
                Your Information Stays <span className="text-brand-teal">Safe</span>{" "}
                With Us
              </h2>
              <p className="reveal-scroll delay-2 pt-4 text-[15px] leading-6 text-[#475569]">
                We collect and process your personal and financial
                information as per the Digital Personal Data Protection
                (DPDP) Act, 2023. Your data is used only with your prior
                consent and is never shared with third parties without your
                permission.
              </p>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-[#f4f2fd] px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#176766]">
                  <FiLock className="h-4 w-4" />
                </span>
                <span className="text-xs font-semibold leading-4 text-[#1c1917]">
                  256-bit SSL
                  <br />
                  Encryption
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-[#f4f2fd] px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-brand-purple">
                  <FiShield className="h-4 w-4" />
                </span>
                <span className="text-xs font-semibold leading-4 text-[#1c1917]">
                  ISO
                  <br />
                  Certified
                </span>
              </div>
            </div>

            {/* Built on Trust card */}
            <div
              className="relative mt-2 flex min-h-[260px] flex-col justify-center overflow-hidden rounded-[24px] bg-cover bg-center p-8"
              style={{ backgroundImage: `url(${trustCardBg})` }}
            >
              <div className="relative max-w-[280px]">
                <span className="text-xs font-semibold uppercase tracking-[0.6px] text-white/60">
                  Built on Trust
                </span>
                <h3 className="pt-3 text-2xl font-bold leading-tight text-white sm:text-3xl">
                  Safe Data
                  <br />
                  Brighter Possibilities
                </h3>
                <span className="mt-4 block h-[2px] w-8 bg-white/40" />
                <p className="mt-3 text-sm leading-5 text-white/75">
                  Your privacy enables a more secure and inclusive financial
                  future.
                </p>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-5">
            {/* Wide DPDP banner card */}
            <div className="relative grid grid-cols-1 gap-4 overflow-hidden rounded-[20px] border border-solid border-[#d7ece5] bg-[#eff9f5] p-6 sm:grid-cols-[1fr_240px] sm:items-center">
              <div>
                <span className="text-xs font-semibold text-[#176766]">01 —</span>
                <h3 className="pt-1 text-xl font-bold leading-tight text-[#1c1917] sm:text-2xl">
                  Compliant with DPDP Act, 2023
                </h3>
                <p className="pt-2 text-sm leading-5 text-[#475569]">
                  We follow the Digital Personal Data Protection (DPDP) Act,
                  2023 and collect your data only with your prior consent.
                </p>
              </div>
              <img
                src={facadeImg}
                alt=""
                className="hidden h-full min-h-[140px] w-full rounded-xl object-cover sm:block"
              />
            </div>

            {/* Middle row */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="rounded-[20px] bg-[#f6f3fd] p-6">
                <span className="text-xs font-semibold text-[#7c3aed]">02 —</span>
                <h3 className="pt-2 text-lg font-bold leading-tight text-[#1c1917]">
                  Your Data
                  <br />
                  Stays Private
                </h3>
                <p className="pt-2 text-sm leading-5 text-[#475569]">
                  We never sell your personal information. Your financial
                  details are kept strictly confidential.
                </p>
              </div>
              <div className="rounded-[20px] bg-[#eff9f5] p-6">
                <span className="text-xs font-semibold text-[#176766]">03 —</span>
                <h3 className="pt-2 text-lg font-bold leading-tight text-[#1c1917]">
                  Transparent
                  <br />& Clear
                </h3>
                <p className="pt-2 text-sm leading-5 text-[#475569]">
                  No hidden charges. Full loan terms and interest rates are
                  shown upfront before you e-sign.
                </p>
              </div>
            </div>

            {/* Bottom row */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="rounded-[20px] bg-[#eff9f5] p-6">
                <span className="text-xs font-semibold text-[#176766]">04 —</span>
                <h3 className="pt-2 text-lg font-bold leading-tight text-[#1c1917]">
                  Secure & Trusted
                </h3>
                <p className="pt-2 text-sm leading-5 text-[#475569]">
                  Your information is protected with industry-standard
                  security and verified partners.
                </p>
              </div>

              {/* Decorative wave card */}
              <div
                className="relative overflow-hidden rounded-[20px] bg-cover bg-center p-6"
                style={{ backgroundImage: `url(${waveCardBg})` }}
              >
                <div className="relative flex flex-col gap-1">
                  <span className="text-base font-semibold text-white">
                    People
                  </span>
                  <span className="text-base font-semibold text-white">
                    Privacy
                  </span>
                  <span className="text-base font-semibold text-white">
                    Progress
                  </span>
                  <span className="mt-3 block h-[2px] w-8 bg-white/40" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer banner */}
        <div className="mt-10 flex items-start gap-2 border-t border-solid border-[#d7ece5] pt-6">
          <FiInfo className="mt-[2px] h-4 w-4 shrink-0 text-[#ea580c]" />
          <p className="text-xs leading-5 text-[#9a5b1e]">
            <span className="font-bold uppercase">Important:</span> We only
            use verified claims like "encrypted", "bank-grade security",
            "RBI regulated", etc. if we are dealing directly with partner
            bank systems.
          </p>
        </div>
      </div>
    </section>
  );
}
